import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Home, Users, CalendarDays, Clock3, MapPin, CheckCircle2, Circle, XCircle,
  ArrowRight, ArrowLeft, ShieldCheck, Sparkles, Plus, Pencil,
  Award, Info, X,
} from "lucide-react";
import CONFIG, { getUserCity } from "../../../config";
import { getServiceImage } from "../../../config/serviceImages";
import { DEEP_CLEANING_SCOPE } from "../../../config/pricing";
import { isUserLoggedIn, getAuthMobile, getAuthToken } from "../../common/Header";
import CurrentLocationDetector from "../../common/CurrentLocationDetector";
import CurrentLocationCard from "../../common/CurrentLocationCard";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const HERO_IMAGE   = getServiceImage("Full Home Deep Cleaning");
// Sentinel selectedAddressId value meaning "use the detected current
// location" rather than one of the customer's saved addresses.
const CURRENT_LOCATION_ID = "__current_location__";
// City is a fixed, config-driven selection (CONFIG.ACTIVE_CITIES/CITIES) —
// never free text — so a saved/booking address.city always matches the
// canonical slug partner-matching compares against (see the city-alias
// bug this fixed on the backend: special_service_assign_common.py).
const CITY_OPTIONS = (CONFIG.ACTIVE_CITIES || []).map((slug) => ({
  value: slug, label: CONFIG.CITIES?.[slug]?.label || slug,
}));

const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const authHeaders = () => ({
  "Content-Type": "application/json",
  ...(getAuthToken() ? { Authorization: `Bearer ${getAuthToken()}` } : {}),
});

// ─────────────────────────────────────────────────────────────────────────────
// STATIC CONTENT
// ─────────────────────────────────────────────────────────────────────────────
const STEPS = [
  { key: "home",     label: "Your Home",      sub: "Tell us about your home" },
  { key: "plan",     label: "Plan",           sub: "Choose your plan"        },
  { key: "included", label: "What's Included", sub: "Expand to see details"  },
  { key: "schedule", label: "Schedule",       sub: "Date & time"             },
  { key: "review",   label: "Summary",        sub: "Address, confirm & pay"  },
];

const HOME_TYPES     = [
  "Residential Apartment Building",
  "Apartment Complex",
  "Multi-Sector Residential Building",
  "Villa/Duplex",
  "Independent House",
];
const PROPERTY_TYPES = ["Furnished", "Semi-Furnished", "Unfurnished"];

// Pricing (home-size table + Standard/Premium plans) is admin-editable in
// config.js: CONFIG.SERVICE_CONFIG["special-services"].services["deep-cleaning"].
// Premium's price per size = that size's standardPrice + the premium plan's
// surcharge (both come straight from config — no math hardcoded here).
const DEEP_CLEANING_CONFIG = CONFIG.SERVICE_CONFIG?.["special-services"]?.services?.["deep-cleaning"] || {};
const PREMIUM_PLAN = DEEP_CLEANING_CONFIG.plans?.find((p) => p.id === "premium");

const BHK_OPTIONS = (DEEP_CLEANING_CONFIG.sizes || []).map((b) => ({
  ...b,
  premiumPrice: b.standardPrice + Number(PREMIUM_PLAN?.surcharge || 0),
}));

const PLANS = DEEP_CLEANING_CONFIG.plans || [];

const BOOKING_WINDOW_DAYS = DEEP_CLEANING_CONFIG.bookingWindowDays || 6;

const TRUST_BULLETS = [
  { icon: Users,       text: "Professional Team"       },
  { icon: Sparkles,    text: "Cleaning Equipment Included" },
  { icon: ShieldCheck, text: "Eco-friendly Products"    },
  { icon: Award,       text: "Satisfaction Guarantee"   },
];

// Duration-based slots — pick how long the visit should be, then an exact
// time window within that duration. Same pattern/time windows as Rapid
// Cleaning's Schedule step (RapidCleaningPage.js) — duration here is a
// scheduling choice only, not a pricing lever (Deep Cleaning's price is
// already fully determined by BHK size + Plan from Steps 1-2).
const DURATION_OPTIONS = [
  { id: "4hr", label: "4 Hours" },
  { id: "6hr", label: "6 Hours" },
];
const TIME_SLOTS_BY_DURATION = {
  "4hr": [
    { id: "4hr_0700_1100", label: "7:00 AM – 11:00 AM" },
    { id: "4hr_1200_1600", label: "12:00 PM – 4:00 PM" },
    { id: "4hr_1700_2100", label: "5:00 PM – 9:00 PM"  },
  ],
  "6hr": [
    { id: "6hr_0700_1300", label: "7:00 AM – 1:00 PM" },
    { id: "6hr_1400_2000", label: "2:00 PM – 8:00 PM" },
  ],
};

const ADDR_LABEL_ICONS = { Home: "🏠", Work: "🏢", Office: "🏢", Other: "📍" };

// Scope of work shown behind the Step 1 "View Details" popup — grouped by
// room/category, each with its own reference photo.
const SCOPE_INCLUDED = [
  { category: "Living Room", image: getServiceImage("Living Room Cleaning"), points: ["Dust and wipe all furniture"] },
  { category: "Bedroom",     image: getServiceImage("Bedroom Cleaning"),     points: ["Mattress cleaning"] },
];

const SCOPE_EXCLUDED = [
  { category: "Furniture & Fabric", image: getServiceImage("Sofa Cleaning"), points: ["Sofa shampooing"] },
];

// Booking starts from tomorrow (no same-day slots) and stays open for
// BOOKING_WINDOW_DAYS days from there — both the chips and the custom
// date picker's min/max are driven off this same window.
const buildDateChips = () => {
  const chips = [];
  for (let i = 1; i <= BOOKING_WINDOW_DAYS; i++) {
    const d = new Date();
    d.setDate(d.getDate() + i);
    const iso = d.toISOString().slice(0, 10);
    const label = i === 1 ? "Tomorrow"
      : d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
    chips.push({ iso, label });
  }
  return chips;
};

const formatDateLabel = (iso) => {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" });
};

// ─────────────────────────────────────────────────────────────────────────────
const DeepCleaningPage = () => {
  const navigate = useNavigate();
  const mobile   = getAuthMobile();

  const [step, setStep] = useState("home");
  const stepIndex = STEPS.findIndex((s) => s.key === step);

  // ── Step 1: Home details ──────────────────────────────────────────────────
  const [homeType,     setHomeType]     = useState("");
  const [propertyType, setPropertyType] = useState("");
  const [bhk,           setBhk]         = useState("");
  const [city]          = useState(getUserCity() || "bengaluru");
  const [area,           setArea]       = useState("");
  const [showScopeModal, setShowScopeModal] = useState(false);

  const areaOptions = useMemo(() => {
    const entry = CONFIG.AREA_OPTIONS?.[city] || CONFIG.AREA_OPTIONS?.[city?.toLowerCase()];
    return entry?.areas || [];
  }, [city]);

  const bhkMeta = BHK_OPTIONS.find((b) => b.value === bhk) || null;

  // ── Step 2: Plan ───────────────────────────────────────────────────────────
  const [plan, setPlan] = useState("");

  const planMeta = PLANS.find((p) => p.id === plan) || null;
  const planPrice = bhkMeta && plan ? bhkMeta[`${plan}Price`] : 0;

  // ── Step 3: What's Included / Excluded — driven by the Step 2 plan ───────
  const scope = DEEP_CLEANING_SCOPE[plan] || null;

  // ── Step 4: Schedule ──────────────────────────────────────────────────────
  const dateChips = useMemo(() => buildDateChips(), []);
  const [selectedDate,   setSelectedDate]   = useState("");
  const [customDateOpen, setCustomDateOpen] = useState(false);
  const [duration,        setDuration]       = useState(""); // "4hr" | "6hr"
  const [slot,             setSlot]          = useState("");

  const durationMeta    = DURATION_OPTIONS.find((d) => d.id === duration) || null;
  const timeSlotChoices  = TIME_SLOTS_BY_DURATION[duration] || [];
  const sessionMeta      = timeSlotChoices.find((t) => t.id === slot) || null;

  // Picking a different duration invalidates whatever specific time slot
  // was chosen under the old duration.
  const chooseDuration = (id) => { setDuration(id); setSlot(""); };

  // Estimated time scales with home size (bigger home = longer visit
  // regardless of plan); team size scales with plan (deeper scope = more
  // hands), per the pricing table's two separate axes.
  const estimatedTime = bhkMeta?.timeLabel || "";

  // ── Step 5: Address ───────────────────────────────────────────────────────
  const [addresses,        setAddresses]        = useState([]);
  const [loadingAddresses, setLoadingAddresses]  = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState("");
  // Detected-and-editable GPS address — kept separate from the saved-
  // addresses list (not persisted to the profile); selecting it vs. a
  // saved address is just which one selectedAddressId currently points at.
  const [currentLocationAddr, setCurrentLocationAddr] = useState(null);
  const [showAddForm,      setShowAddForm]       = useState(false);
  const [savingAddr,       setSavingAddr]        = useState(false);
  const [addrFormError,    setAddrFormError]     = useState("");
  const [instructions,     setInstructions]      = useState("");
  const [newAddr, setNewAddr] = useState({
    label: "Home", line1: "", area: "", city, state: "", pincode: "", map_url: "",
  });

  useEffect(() => {
    if (!mobile) return;
    let cancelled = false;
    (async () => {
      setLoadingAddresses(true);
      try {
        const res  = await fetch(`${API_BASE_URL}/api/user/user-addresses/${encodeURIComponent(mobile)}/`, { headers: authHeaders() });
        const data = await res.json();
        const list = Array.isArray(data?.data) ? data.data : [];
        if (!cancelled) {
          setAddresses(list);
          if (list.length) setSelectedAddressId(list[0].id);
        }
      } catch { /* saved addresses are optional — user can still add one manually */ }
      finally { if (!cancelled) setLoadingAddresses(false); }
    })();
    return () => { cancelled = true; };
  }, [mobile]);

  const newAddrAreaOptions = useMemo(() => {
    const entry = CONFIG.AREA_OPTIONS?.[newAddr.city] || CONFIG.AREA_OPTIONS?.[newAddr.city?.toLowerCase()];
    return entry?.areas || [];
  }, [newAddr.city]);

  // Reverse-geocodes the detected GPS point into a full, editable address
  // (see CurrentLocationDetector.js) and makes it its own selectable
  // option alongside the saved addresses below — NOT funneled into the
  // "Add New Address" form, and not persisted to the profile, since it's
  // only relevant to this one booking.
  const handleLocationDetected = (addr) => {
    setCurrentLocationAddr(addr);
    setSelectedAddressId(CURRENT_LOCATION_ID);
    setShowAddForm(false);
  };

  const handleAddAddress = async () => {
    setAddrFormError("");
    if (!newAddr.line1.trim() || !newAddr.city || !newAddr.area || !newAddr.pincode.trim()) {
      setAddrFormError("Please fill in the house/flat details, city, area and pincode.");
      return;
    }
    if (addresses.length >= 3) {
      setAddrFormError("You already have 3 saved addresses — remove one from your profile first.");
      return;
    }
    if (!mobile) {
      navigate("/user/user-login", { state: { redirectTo: "/service/special-service/deep-cleaning" } });
      return;
    }
    const entry = {
      id: `addr-${Date.now()}`, label: newAddr.label, line1: newAddr.line1.trim(),
      area: newAddr.area, city: newAddr.city, state: newAddr.state.trim(),
      pincode: newAddr.pincode.trim(), map_url: newAddr.map_url || "",
    };
    const updated = [...addresses, entry];
    setSavingAddr(true);
    try {
      const res  = await fetch(`${API_BASE_URL}/api/user/user-addresses/${encodeURIComponent(mobile)}/`, {
        method: "PATCH", headers: authHeaders(), body: JSON.stringify({ addresses: updated }),
      });
      const data = await res.json();
      if (!res.ok || data?.success === false) throw new Error(data?.message || "Failed to save address.");
      const saved = Array.isArray(data?.data) ? data.data : updated;
      setAddresses(saved);
      const savedEntry = saved.find((a) => a.line1 === entry.line1 && a.pincode === entry.pincode) || saved[saved.length - 1];
      setSelectedAddressId(savedEntry?.id || entry.id);
      setShowAddForm(false);
      setNewAddr({ label: "Home", line1: "", area: "", city, state: "", pincode: "", map_url: "" });
    } catch (e) {
      setAddrFormError(e.message || "Failed to save address.");
    } finally {
      setSavingAddr(false);
    }
  };

  const selectedAddress = selectedAddressId === CURRENT_LOCATION_ID
    ? currentLocationAddr
    : addresses.find((a) => a.id === selectedAddressId) || null;

  // ── Pricing (mirrors the standard checkout charge formula) ───────────────
  const CHARGES         = CONFIG.CHECKOUT_CHARGES ?? { DISCOUNT_PERCENT: 5, GST_PERCENT: 18, PLATFORM_FEE: 20 };
  const PARTIAL_THRESHOLD = CONFIG.PARTIAL_PAYMENT_THRESHOLD ?? 1000;
  const subtotal   = planPrice;
  const discount   = Math.round(subtotal * (CHARGES.DISCOUNT_PERCENT / 100));
  const taxableAmt = subtotal - discount;
  const gst         = Math.round(taxableAmt * (CHARGES.GST_PERCENT / 100));
  const platformFee = Number(CHARGES.PLATFORM_FEE ?? 0);
  const total        = taxableAmt + gst + platformFee;
  const canPayLater   = subtotal >= PARTIAL_THRESHOLD;

  const [paymentOption, setPaymentOption] = useState("pay_now");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [placing,       setPlacing]       = useState(false);
  const [placeError,    setPlaceError]    = useState("");

  useEffect(() => { if (!canPayLater) setPaymentOption("pay_now"); }, [canPayLater]);

  // ── Step validation / navigation ──────────────────────────────────────────
  const canContinue = {
    home:     Boolean(homeType && propertyType && bhk && area),
    plan:     Boolean(plan),
    included: true,
    // Address is picked on the Summary step now, not here.
    schedule: Boolean(selectedDate && duration && slot),
  };

  const goNext = () => {
    const idx = STEPS.findIndex((s) => s.key === step);
    if (!canContinue[step]) return;
    if (idx < STEPS.length - 1) setStep(STEPS[idx + 1].key);
  };
  const goBack = () => {
    const idx = STEPS.findIndex((s) => s.key === step);
    if (idx > 0) setStep(STEPS[idx - 1].key); else navigate(-1);
  };

  // ── Place order ────────────────────────────────────────────────────────────
  // Deep Cleaning bookings are stored in their own collection
  // (special_service_deepCleaning) via a dedicated endpoint, not the
  // generic cart place-order pipeline — see book_deep_cleaning() /
  // deep_cleaning_booking_detail() in the backend. `items` /
  // `delivery_address` / `total_amount` below are display-only fields
  // consumed by PaymentPage.js's shared order-summary UI; the backend
  // reads `address`/`pricing` instead.
  const handlePlaceOrder = async () => {
    if (!agreedToTerms || !selectedAddressId || placing) return;
    if (!isUserLoggedIn() || !mobile) {
      navigate("/user/user-login", { state: { redirectTo: "/service/special-service/deep-cleaning" } });
      return;
    }

    const item = {
      id: "deep_cleaning", service_id: "deep_cleaning",
      title: `Deep Cleaning – ${planMeta?.name} Package`,
      name:  `Deep Cleaning – ${planMeta?.name} Package`,
      price: subtotal, qty: 1,
      image: HERO_IMAGE,
    };

    const bookingPayload = {
      mobile,
      home_details: {
        home_type: homeType, property_type: propertyType,
        bhk, bhk_label: bhkMeta?.label, area, city,
      },
      plan: { id: plan, name: planMeta?.name, price: planPrice, team: planMeta?.team },
      addons: [],
      schedule: {
        date: selectedDate, date_label: formatDateLabel(selectedDate),
        duration, duration_label: durationMeta?.label,
        slot, slot_label: sessionMeta?.label, time_range: sessionMeta?.label,
        team: planMeta?.team, estimated_time: estimatedTime,
      },
      address: selectedAddress || null,
      instructions,
      pricing: { subtotal, discount, gst, platform_fee: platformFee, total },
      payment_option: paymentOption,
      // Display-only for PaymentPage.js's shared order-summary UI:
      items: [item],
      delivery_address: selectedAddress || null,
      total_amount: total,
    };

    setPlacing(true);
    setPlaceError("");

    if (paymentOption === "pay_after_work") {
      try {
        const res  = await fetch(`${API_BASE_URL}/api/special-service/deep-cleaning/book/`, {
          method: "POST", credentials: "include", headers: authHeaders(), body: JSON.stringify(bookingPayload),
        });
        const data = await res.json();
        if (!res.ok || !data?.success) throw new Error(data?.message || "Failed to place booking.");
        navigate("/profile-dashboard", {
          replace: true,
          state: { openBookings: true, orderPlaced: true, orderId: data?.data?.process_id || "", total, mobile },
        });
      } catch (e) {
        setPlaceError(e.message || "Failed to place booking. Please try again.");
      } finally {
        setPlacing(false);
      }
      return;
    }

    try {
      const res  = await fetch(`${API_BASE_URL}/api/payment/create-order/`, {
        method: "POST", headers: authHeaders(), body: JSON.stringify({ amount: total, mobile }),
      });
      const data = await res.json();
      if (!res.ok || !data?.success) throw new Error(data?.message || "Could not initiate payment.");
      navigate("/payment", {
        state: {
          paymentOrder: data,
          orderPayload: { ...bookingPayload, __submit_endpoint: "/api/special-service/deep-cleaning/book/" },
          breakdown: { subtotal, discount, gst, platformFee, tip: 0, total },
        },
      });
    } catch (e) {
      setPlaceError(e.message || "Payment initiation failed. Please try again.");
    } finally {
      setPlacing(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      <div style={S.container}>
        <div style={S.breadcrumb}>
          <Link to="/" style={S.breadcrumbLink}>Home</Link> <span>&gt;</span>{" "}
          <Link to="/services/special-services" style={S.breadcrumbLink}>Special Services</Link> <span>&gt;</span>{" "}
          <span>Deep Cleaning</span>
        </div>

        <div style={S.hero}>
          <div style={S.heroBadge}><Sparkles size={13} /> DEEP HOME CLEANING</div>
          <h1 style={S.heroTitle}>Full Home Deep Cleaning</h1>
          <p style={S.heroText}>Intensive, top-to-bottom cleaning for your home — trained teams, transparent pricing.</p>
        </div>

        {/* Step progress */}
        <div style={S.stepRow}>
          {STEPS.map((s, i) => (
            <React.Fragment key={s.key}>
              <div style={S.stepItem}>
                <div style={{ ...S.stepCircle, ...(i === stepIndex ? S.stepCircleActive : {}), ...(i < stepIndex ? S.stepCircleDone : {}) }}>
                  {i < stepIndex ? <CheckCircle2 size={14} /> : i + 1}
                </div>
                <div>
                  <div style={{ ...S.stepLabel, ...(i <= stepIndex ? S.stepLabelActive : {}) }}>{s.label}</div>
                  <div style={S.stepSub}>{s.sub}</div>
                </div>
              </div>
              {i < STEPS.length - 1 && <div style={{ ...S.stepConnector, ...(i < stepIndex ? S.stepConnectorActive : {}) }} />}
            </React.Fragment>
          ))}
        </div>

        <button type="button" style={S.backBtn} onClick={goBack}>
          <ArrowLeft size={15} /> Back
        </button>

        {/* ── Step 1: Your Home ─────────────────────────────────────────── */}
        {step === "home" && (
          <div style={S.card}>
            <h2 style={S.sectionTitle}>Tell us about your home</h2>

            <div style={S.fieldGroup}>
              <div style={S.fieldLabel}>Home Type</div>
              <div style={S.radioGrid4}>
                {HOME_TYPES.map((t) => (
                  <button key={t} type="button" style={{ ...S.radioCard, ...(homeType === t ? S.radioCardActive : {}) }} onClick={() => setHomeType(t)}>
                    {homeType === t ? <CheckCircle2 size={16} color="#4f46e5" /> : <Circle size={16} color="#d1d5db" />}
                    <span>{t}</span>
                  </button>
                ))}
              </div>
            </div>

            <div style={S.fieldGroup}>
              <div style={S.fieldLabel}>Property Type</div>
              <div style={S.radioGrid3}>
                {PROPERTY_TYPES.map((t) => (
                  <button key={t} type="button" style={{ ...S.radioCard, ...(propertyType === t ? S.radioCardActive : {}) }} onClick={() => setPropertyType(t)}>
                    {propertyType === t ? <CheckCircle2 size={16} color="#4f46e5" /> : <Circle size={16} color="#d1d5db" />}
                    <span>{t}</span>
                  </button>
                ))}
              </div>
            </div>

            <div style={S.fieldGroup}>
              <div style={S.fieldLabel}>Home Size</div>
              <div style={S.radioGrid4}>
                {BHK_OPTIONS.map((b) => (
                  <button key={b.value} type="button" style={{ ...S.radioCard, ...S.bhkCard, ...(bhk === b.value ? S.radioCardActive : {}) }} onClick={() => setBhk(b.value)}>
                    <div style={S.bhkCardTop}>
                      {bhk === b.value ? <CheckCircle2 size={16} color="#4f46e5" /> : <Circle size={16} color="#d1d5db" />}
                      <span>{b.label}</span>
                    </div>
                    <span style={S.bhkCardSub}>{b.areaLabel} · {b.timeLabel}</span>
                  </button>
                ))}
              </div>
            </div>

            <div style={S.fieldGroup}>
              <div style={S.fieldLabel}>Approx. Area ({city.charAt(0).toUpperCase() + city.slice(1)})</div>
              <select style={S.select} value={area} onChange={(e) => setArea(e.target.value)}>
                <option value="">Select your locality</option>
                {areaOptions.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>

            <div style={S.scopeBanner}>
              <img src={HERO_IMAGE} alt="What's included in Deep Cleaning" style={S.scopeBannerImg} />
              <div style={S.scopeBannerOverlay}>
                <div style={S.scopeBannerText}>Know exactly what's included — and what's not</div>
                <button type="button" style={S.scopeDetailsBtn} onClick={() => setShowScopeModal(true)}>
                  <Info size={14} /> Details
                </button>
              </div>
            </div>

            <button type="button" style={{ ...S.continueBtn, ...(!canContinue.home ? S.continueBtnDisabled : {}) }} disabled={!canContinue.home} onClick={goNext}>
              Continue <ArrowRight size={16} />
            </button>
          </div>
        )}

        {/* ── Step 2: Plan ──────────────────────────────────────────────── */}
        {step === "plan" && (
          <div style={S.card}>
            <h2 style={S.sectionTitle}>Choose your cleaning plan</h2>

            {bhkMeta && (
              <div style={S.sizeNote}>
                <MapPin size={13} /> Pricing for {bhkMeta.label} ({bhkMeta.areaLabel})
              </div>
            )}

            <div style={S.planGrid}>
              {PLANS.map((p) => {
                const selected = plan === p.id;
                const price = bhkMeta ? bhkMeta[`${p.id}Price`] : null;
                return (
                  <div key={p.id} style={{ ...S.planCard, ...(selected ? S.planCardActive : {}) }}>
                    {p.badge && <div style={S.planBadge}>{p.badge}</div>}
                    <div style={S.planName}>{p.name}</div>
                    <div style={S.planPrice}>{price != null ? fmt(price) : "—"}</div>
                    <div style={S.planMetaRow}>
                      <span style={S.planMetaItem}><Clock3 size={12} /> {p.duration}</span>
                      <span style={S.planMetaItem}><Users size={12} /> {p.team}</span>
                    </div>
                    <p style={S.planDesc}>{p.desc}</p>
                    <ul style={S.planFeatureList}>
                      {p.features.map((f) => (
                        <li key={f} style={S.planFeature}><CheckCircle2 size={14} color="#4f46e5" /> {f}</li>
                      ))}
                    </ul>
                    <button type="button" style={{ ...S.planSelectBtn, ...(selected ? S.planSelectBtnActive : {}) }} onClick={() => setPlan(p.id)}>
                      {selected ? "Selected" : "Select"}
                    </button>
                  </div>
                );
              })}
            </div>

            <div style={S.trustStrip}>
              {TRUST_BULLETS.map(({ icon: Icon, text }) => (
                <div key={text} style={S.trustItem}><Icon size={16} color="#4f46e5" /> {text}</div>
              ))}
            </div>

            <button type="button" style={{ ...S.continueBtn, ...(!canContinue.plan ? S.continueBtnDisabled : {}) }} disabled={!canContinue.plan} onClick={goNext}>
              Continue <ArrowRight size={16} />
            </button>
          </div>
        )}

        {/* ── Step 3: What's Included / Excluded (by plan) ────────────────── */}
        {step === "included" && (
          <div style={S.card}>
            <h2 style={S.sectionTitle}>{scope?.title || "What's Included"}</h2>
            <p style={S.scheduleHeadSub}>What's included and excluded for your selected plan.</p>

            {scope ? (
              <>
                <div style={S.doDontGrid}>
                  <div>
                    <div style={S.doDontColTitleInclude}><CheckCircle2 size={15} color="#4f46e5" /> Included</div>
                    <ul style={S.doDontList}>
                      {scope.included.map((p) => (
                        <li key={p} style={S.doDontItem}>
                          <CheckCircle2 size={13} color="#4f46e5" /> <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div style={S.doDontColTitleExclude}><XCircle size={15} color="#dc2626" /> Excluded</div>
                    <ul style={S.doDontList}>
                      {scope.excluded.map((p) => (
                        <li key={p} style={S.doDontItem}>
                          <XCircle size={13} color="#dc2626" /> <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div style={S.notesBox}>
                  <div style={S.notesBoxTitle}><Info size={15} color="#4f46e5" /> Customer Notes</div>
                  <ul style={S.notesList}>
                    {scope.customerNotes.map((n) => (
                      <li key={n} style={S.notesItem}>{n}</li>
                    ))}
                  </ul>
                </div>

                <div style={S.notesBox}>
                  <div style={S.notesBoxTitle}><Sparkles size={15} color="#4f46e5" /> Standard vs Premium — Simple Difference</div>
                  <ul style={S.notesList}>
                    {DEEP_CLEANING_SCOPE.finalDifference.map((n) => (
                      <li key={n} style={S.notesItem}>{n}</li>
                    ))}
                  </ul>
                </div>
              </>
            ) : (
              <p style={S.scheduleHeadSub}>Go back and choose Standard or Premium to see what's included.</p>
            )}

            <button type="button" style={S.continueBtn} onClick={goNext}>
              Continue <ArrowRight size={16} />
            </button>
          </div>
        )}

        {/* ── Step 4: Schedule ──────────────────────────────────────────── */}
        {step === "schedule" && (
          <div style={S.card}>
            <h2 style={S.sectionTitle}>When would you like us to come?</h2>
            <p style={S.scheduleHeadSub}>Bookable from tomorrow, up to {BOOKING_WINDOW_DAYS} days ahead.</p>

            <div style={S.fieldGroup}>
              <div style={S.chipRow}>
                {dateChips.map((c) => (
                  <button key={c.iso} type="button" style={{ ...S.dateChip, ...(selectedDate === c.iso ? S.dateChipActive : {}) }}
                    onClick={() => { setSelectedDate(c.iso); setCustomDateOpen(false); }}>
                    {c.label}
                  </button>
                ))}
                <button type="button" style={{ ...S.dateChip, ...(customDateOpen ? S.dateChipActive : {}) }} onClick={() => setCustomDateOpen((v) => !v)}>
                  <CalendarDays size={14} /> Pick a Date
                </button>
              </div>
              {customDateOpen && (
                <input
                  type="date" style={S.dateInput}
                  min={dateChips[0]?.iso}
                  max={dateChips[dateChips.length - 1]?.iso}
                  value={!dateChips.some((c) => c.iso === selectedDate) ? selectedDate : ""}
                  onChange={(e) => setSelectedDate(e.target.value)}
                />
              )}
            </div>

            <div style={S.fieldGroup}>
              <div style={S.fieldLabel}>Select Duration</div>
              <div style={S.chipRow}>
                {DURATION_OPTIONS.map((d) => (
                  <button key={d.id} type="button" style={{ ...S.dateChip, ...(duration === d.id ? S.dateChipActive : {}) }}
                    onClick={() => chooseDuration(d.id)}>
                    {d.label}
                  </button>
                ))}
              </div>
            </div>

            {duration && (
              <div style={S.fieldGroup}>
                <div style={S.fieldLabel}>Select Time Slot</div>
                <div style={S.slotGrid}>
                  {timeSlotChoices.map((t) => (
                    <button key={t.id} type="button" style={{ ...S.slotChip, ...(slot === t.id ? S.slotChipActive : {}) }} onClick={() => setSlot(t.id)}>
                      <div style={{ ...S.slotChipLabel, ...(slot === t.id ? { color: "#fff" } : {}) }}>{t.label}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {plan && bhkMeta && (
              <div style={S.scheduleInfoRow}>
                <span style={S.scheduleInfoChip}><Users size={14} /> {planMeta?.team}</span>
                <span style={S.scheduleInfoChip}><Clock3 size={14} /> Estimated time: {estimatedTime}</span>
              </div>
            )}

            <button type="button" style={{ ...S.continueBtn, ...(!canContinue.schedule ? S.continueBtnDisabled : {}) }} disabled={!canContinue.schedule} onClick={goNext}>
              Continue to Summary <ArrowRight size={16} />
            </button>
          </div>
        )}

        {/* ── Step 5: Summary — address, instructions, payment, terms ───── */}
        {step === "review" && (
          <div style={S.reviewLayout}>
            <div style={S.reviewLeft}>
              <div style={S.recapCard}>
                <div style={S.recapHead}>
                  <div style={S.recapTitle}><Home size={16} color="#4f46e5" /> Your Home</div>
                  <button type="button" style={S.changeLink} onClick={() => setStep("home")}><Pencil size={12} /> Change</button>
                </div>
                <div style={S.recapText}>{homeType} • {propertyType} • {bhkMeta?.label} • {area}</div>
              </div>

              <div style={S.recapCard}>
                <div style={S.recapHead}>
                  <div style={S.recapTitle}><Sparkles size={16} color="#4f46e5" /> Plan</div>
                  <button type="button" style={S.changeLink} onClick={() => setStep("plan")}><Pencil size={12} /> Change</button>
                </div>
                <div style={S.recapText}>{planMeta?.name} Package — {fmt(planPrice)}</div>
              </div>

              <div style={S.recapCard}>
                <div style={S.recapHead}>
                  <div style={S.recapTitle}><CalendarDays size={16} color="#4f46e5" /> Date &amp; Time</div>
                  <button type="button" style={S.changeLink} onClick={() => setStep("schedule")}><Pencil size={12} /> Change</button>
                </div>
                <div style={S.recapText}>
                  {formatDateLabel(selectedDate)} · {durationMeta?.label} · {sessionMeta?.label}
                </div>
                <div style={S.recapSubText}><Users size={13} /> {planMeta?.team} &nbsp;·&nbsp; <Clock3 size={13} /> {estimatedTime}</div>
              </div>

              <div style={S.recapCard}>
                <div style={S.recapHead}>
                  <div style={S.recapTitle}><MapPin size={16} color="#4f46e5" /> Service Address</div>
                </div>

                <CurrentLocationDetector onDetected={handleLocationDetected}
                  buttonStyle={S.locationBtn} errorStyle={S.errorText}
                  idleLabel="📍 Use my current location" />

                {currentLocationAddr && (
                  <div style={S.fieldGroup}>
                    <CurrentLocationCard
                      addr={currentLocationAddr}
                      onChange={(updated) => { setCurrentLocationAddr(updated); setSelectedAddressId(CURRENT_LOCATION_ID); }}
                      cardStyle={{
                        border: selectedAddressId === CURRENT_LOCATION_ID ? "2px solid #4f46e5" : "1.5px solid #e5e7eb",
                        borderRadius: "14px", padding: "14px",
                        background: selectedAddressId === CURRENT_LOCATION_ID ? "#eef2ff" : "#fff",
                        display: "flex", flexDirection: "column", gap: "10px",
                      }}
                    />
                    {selectedAddressId !== CURRENT_LOCATION_ID && (
                      <button type="button" style={{ ...S.secondaryBtn, marginTop: "8px" }} onClick={() => setSelectedAddressId(CURRENT_LOCATION_ID)}>
                        Use this address
                      </button>
                    )}
                  </div>
                )}

                {!mobile ? (
                  <div style={S.stateBox}>
                    <Link to="/user/user-login" state={{ redirectTo: "/service/special-service/deep-cleaning" }} style={S.inlineLink}>Log in</Link> to use a saved address, or add one below.
                  </div>
                ) : loadingAddresses ? (
                  <div style={S.stateBox}>Loading your addresses…</div>
                ) : addresses.length > 0 && (
                  <div style={S.fieldGroup}>
                    <div style={S.fieldLabel}>Saved Addresses</div>
                    <div style={S.addressList}>
                      {addresses.map((addr) => {
                        const selected = selectedAddressId === addr.id;
                        return (
                          <button key={addr.id} type="button" style={{ ...S.addressRow, ...(selected ? S.addressRowActive : {}) }} onClick={() => setSelectedAddressId(addr.id)}>
                            {selected ? <CheckCircle2 size={18} color="#4f46e5" /> : <Circle size={18} color="#d1d5db" />}
                            <span style={S.addressIcon}>{ADDR_LABEL_ICONS[addr.label] || "📍"}</span>
                            <div style={S.addressBody}>
                              <div style={S.addressLabel}>{addr.label}</div>
                              <div style={S.addressText}>{[addr.line1, addr.area, addr.city].filter(Boolean).join(", ")}</div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {!showAddForm ? (
                  <button type="button" style={S.addAddressBtn} onClick={() => setShowAddForm(true)}>
                    <Plus size={15} /> Add New Address
                  </button>
                ) : (
                  <div style={S.addAddressForm}>
                    <div style={S.fieldLabel}>New Address</div>
                    <div style={S.radioGrid3}>
                      {["Home", "Work", "Other"].map((l) => (
                        <button key={l} type="button" style={{ ...S.radioCard, ...(newAddr.label === l ? S.radioCardActive : {}) }} onClick={() => setNewAddr((p) => ({ ...p, label: l }))}>
                          {ADDR_LABEL_ICONS[l]} {l}
                        </button>
                      ))}
                    </div>
                    <input style={S.input} placeholder="House / Flat No., Building, Street" value={newAddr.line1}
                      onChange={(e) => setNewAddr((p) => ({ ...p, line1: e.target.value }))} />
                    <select style={S.select} value={newAddr.city} onChange={(e) => setNewAddr((p) => ({ ...p, city: e.target.value, area: "" }))}>
                      <option value="">Select city</option>
                      {CITY_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                    <div style={S.formRow2}>
                      <select style={S.select} value={newAddr.area} onChange={(e) => setNewAddr((p) => ({ ...p, area: e.target.value }))}>
                        <option value="">Select area</option>
                        {newAddrAreaOptions.map((a) => <option key={a} value={a}>{a}</option>)}
                      </select>
                      <input style={S.input} placeholder="Pincode" value={newAddr.pincode}
                        onChange={(e) => setNewAddr((p) => ({ ...p, pincode: e.target.value }))} />
                    </div>
                    <input style={S.input} placeholder="State (optional)" value={newAddr.state}
                      onChange={(e) => setNewAddr((p) => ({ ...p, state: e.target.value }))} />
                    {addrFormError && <div style={S.errorText}>{addrFormError}</div>}
                    <div style={S.formActions}>
                      <button type="button" style={S.secondaryBtn} onClick={() => { setShowAddForm(false); setAddrFormError(""); }}>Cancel</button>
                      <button type="button" style={S.primaryBtnSm} onClick={handleAddAddress} disabled={savingAddr}>
                        {savingAddr ? "Saving…" : "Save Address"}
                      </button>
                    </div>
                  </div>
                )}

                <div style={{ ...S.fieldGroup, marginBottom: 0 }}>
                  <div style={S.fieldLabel}>Special Instructions (optional)</div>
                  <textarea style={S.textarea} rows={3} placeholder="Gate code, parking info, areas to focus on…" value={instructions}
                    onChange={(e) => setInstructions(e.target.value)} />
                </div>
              </div>

              <div style={S.recapCard}>
                <div style={S.fieldLabel}>Payment</div>
                <div style={S.paymentOptions}>
                  <button type="button" style={{ ...S.payOptionBtn, ...(paymentOption === "pay_now" ? S.payOptionBtnActive : {}) }} onClick={() => setPaymentOption("pay_now")}>
                    {paymentOption === "pay_now" ? <CheckCircle2 size={16} color="#4f46e5" /> : <Circle size={16} color="#d1d5db" />}
                    <div>
                      <div style={S.payOptionTitle}>Pay Now</div>
                      <div style={S.payOptionSub}>Pay the full amount upfront</div>
                    </div>
                  </button>
                  <button type="button" disabled={!canPayLater}
                    style={{ ...S.payOptionBtn, ...(paymentOption === "pay_after_work" ? S.payOptionBtnActive : {}), ...(!canPayLater ? S.payOptionBtnDisabled : {}) }}
                    onClick={() => canPayLater && setPaymentOption("pay_after_work")}>
                    {paymentOption === "pay_after_work" ? <CheckCircle2 size={16} color="#4f46e5" /> : <Circle size={16} color="#d1d5db" />}
                    <div>
                      <div style={S.payOptionTitle}>Pay After Work</div>
                      <div style={S.payOptionSub}>{canPayLater ? "Pay once the job is done" : `Available above ${fmt(PARTIAL_THRESHOLD)}`}</div>
                    </div>
                  </button>
                </div>
              </div>

              {placeError && <div style={S.errorBox}>{placeError}</div>}

              <label style={S.termsWrap}>
                <input type="checkbox" checked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)} />
                <span>
                  I agree to the{" "}
                  <a href="/legal-information" style={S.inlineLink}>Terms &amp; Conditions</a> and{" "}
                  <a href="/legal-information" style={S.inlineLink}>Privacy Policy</a>
                </span>
              </label>
            </div>

            <div style={S.reviewRight}>
              <div style={S.priceCard}>
                <img src={HERO_IMAGE} alt="Deep Cleaning" style={S.priceCardImg} />
                <div style={S.priceCardTitle}>{planMeta?.name} Package</div>

                <div style={S.billRow}><span>Plan ({bhkMeta?.label})</span><span>{fmt(planPrice)}</span></div>
                <div style={S.billDivider} />
                <div style={S.billRow}><span>Subtotal</span><span>{fmt(subtotal)}</span></div>
                <div style={S.billRow}><span>Discount ({CHARGES.DISCOUNT_PERCENT}%)</span><span style={{ color: "#16a34a" }}>-{fmt(discount)}</span></div>
                <div style={S.billRow}><span>GST + Other Charges</span><span>{fmt(gst + platformFee)}</span></div>
                <div style={S.billDivider} />
                <div style={S.totalRow}><span>Total Payable</span><span>{fmt(total)}</span></div>

                <button type="button" style={{ ...S.continueBtn, ...((!agreedToTerms || !selectedAddressId || placing) ? S.continueBtnDisabled : {}) }}
                  disabled={!agreedToTerms || !selectedAddressId || placing} onClick={handlePlaceOrder}>
                  {placing ? "Placing Order…" : paymentOption === "pay_now" ? "Proceed to Payment" : "Confirm Booking"}
                  {!placing && <ArrowRight size={16} />}
                </button>
                {!selectedAddressId && (
                  <div style={{ ...S.recapSubText, color: "#4f46e5", justifyContent: "center", marginBottom: "10px" }}>
                    Select or add a service address above to continue
                  </div>
                )}
                <div style={S.secureNote}><ShieldCheck size={13} color="#4f46e5" /> Secure checkout • Verified professionals • No hidden charges</div>
              </div>
            </div>
          </div>
        )}

        {showScopeModal && (
          <div style={S.modalOverlay} onClick={() => setShowScopeModal(false)}>
            <div style={S.modalCard} onClick={(e) => e.stopPropagation()}>
              <button type="button" style={S.modalCloseBtn} onClick={() => setShowScopeModal(false)}>
                <X size={16} />
              </button>
              <h2 style={S.modalTitle}>What's Included &amp; Not Included</h2>

              <div style={S.modalSection}>
                <div style={S.modalSectionHead}><CheckCircle2 size={18} color="#16a34a" /> What's Included</div>
                {SCOPE_INCLUDED.map((cat) => (
                  <div key={cat.category} style={S.modalCatRow}>
                    <img src={cat.image} alt={cat.category} style={S.modalCatImg} />
                    <div>
                      <div style={S.modalCatName}>{cat.category}</div>
                      <ul style={S.modalCatList}>
                        {cat.points.map((p) => <li key={p} style={S.modalCatPoint}>{p}</li>)}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>

              <div style={S.modalSection}>
                <div style={{ ...S.modalSectionHead, color: "#dc2626" }}><XCircle size={18} color="#dc2626" /> What's Not Included</div>
                {SCOPE_EXCLUDED.map((cat) => (
                  <div key={cat.category} style={S.modalCatRow}>
                    <img src={cat.image} alt={cat.category} style={S.modalCatImg} />
                    <div>
                      <div style={S.modalCatName}>{cat.category}</div>
                      <ul style={S.modalCatList}>
                        {cat.points.map((p) => <li key={p} style={S.modalCatPoint}>{p}</li>)}
                      </ul>
                    </div>
                  </div>
                ))}
                <div style={S.modalNote}><Info size={13} /> Not-included items can be booked as a separate service.</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// STYLES — indigo (#4f46e5) accent, matching this service's card color on
// the Special Services listing page.
// ─────────────────────────────────────────────────────────────────────────────
const S = {
  page:      { background: "#fafafa", minHeight: "100vh", padding: "28px 20px 60px", fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif", color: "#111827" },
  container: { maxWidth: "1100px", margin: "0 auto" },
  breadcrumb:     { fontSize: "13px", color: "#9ca3af", marginBottom: "22px", display: "flex", gap: "6px" },
  breadcrumbLink: { color: "#9ca3af", textDecoration: "none" },

  hero:      { textAlign: "center", marginBottom: "28px" },
  heroBadge: { display: "inline-flex", alignItems: "center", gap: "6px", background: "#eef2ff", color: "#4f46e5", fontSize: "12px", fontWeight: 800, letterSpacing: "0.05em", padding: "6px 12px", borderRadius: "999px", marginBottom: "12px" },
  heroTitle: { margin: "0 0 8px", fontSize: "32px", fontWeight: 900 },
  heroText:  { margin: 0, fontSize: "15px", color: "#6b7280" },

  stepRow: { display: "flex", alignItems: "flex-start", justifyContent: "center", gap: "4px", margin: "0 0 24px", flexWrap: "wrap" },
  stepItem: { display: "flex", alignItems: "center", gap: "8px" },
  stepCircle: { width: "30px", height: "30px", borderRadius: "50%", background: "#e5e7eb", color: "#6b7280", fontSize: "12px", fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  stepCircleActive: { background: "#4f46e5", color: "#fff" },
  stepCircleDone:   { background: "#e0e7ff", color: "#4f46e5" },
  stepLabel: { fontSize: "13px", fontWeight: 700, color: "#9ca3af", lineHeight: 1.3 },
  stepLabelActive: { color: "#111827" },
  stepSub: { fontSize: "11px", color: "#9ca3af" },
  stepConnector: { width: "34px", height: "2px", background: "#e5e7eb", marginTop: "15px", flexShrink: 0 },
  stepConnectorActive: { background: "#4f46e5" },

  backBtn: { display: "inline-flex", alignItems: "center", gap: "6px", background: "none", border: "none", color: "#4f46e5", fontWeight: 700, fontSize: "13px", cursor: "pointer", marginBottom: "16px", padding: 0 },

  card: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "20px", padding: "26px", maxWidth: "760px", margin: "0 auto" },
  sectionTitle: { margin: "0 0 20px", fontSize: "22px", fontWeight: 800 },
  scheduleHeadSub: { margin: "-14px 0 20px", fontSize: "13px", color: "#6b7280" },

  fieldGroup: { marginBottom: "22px" },
  fieldLabel: { fontSize: "13px", fontWeight: 700, color: "#374151", marginBottom: "10px", textTransform: "uppercase", letterSpacing: "0.03em" },

  radioGrid4: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: "10px" },
  radioGrid3: { display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: "10px" },
  radioCard: { display: "flex", alignItems: "center", gap: "8px", padding: "12px 14px", borderRadius: "12px", border: "1.5px solid #e5e7eb", background: "#fff", fontSize: "13px", fontWeight: 600, color: "#374151", cursor: "pointer", textAlign: "left" },
  radioCardActive: { borderColor: "#4f46e5", background: "#eef2ff", color: "#3730a3" },
  bhkCard: { flexDirection: "column", alignItems: "flex-start", gap: "4px" },
  bhkCardTop: { display: "flex", alignItems: "center", gap: "8px" },
  bhkCardSub: { fontSize: "11px", fontWeight: 500, color: "#6b7280" },

  select: { width: "100%", padding: "12px 14px", borderRadius: "12px", border: "1.5px solid #e5e7eb", fontSize: "14px", background: "#fff", color: "#111827" },
  input: { width: "100%", padding: "12px 14px", borderRadius: "12px", border: "1.5px solid #e5e7eb", fontSize: "14px", marginBottom: "10px", boxSizing: "border-box" },
  textarea: { width: "100%", padding: "12px 14px", borderRadius: "12px", border: "1.5px solid #e5e7eb", fontSize: "14px", resize: "vertical", boxSizing: "border-box", fontFamily: "inherit" },
  formRow2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "10px" },

  continueBtn: { width: "100%", marginTop: "6px", padding: "15px 0", borderRadius: "12px", background: "#4f46e5", color: "#fff", fontSize: "15px", fontWeight: 800, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" },
  continueBtnDisabled: { opacity: 0.45, cursor: "not-allowed" },

  scopeBanner: { position: "relative", borderRadius: "16px", overflow: "hidden", marginBottom: "20px" },
  scopeBannerImg: { width: "100%", aspectRatio: "16 / 9", objectFit: "cover", display: "block" },
  scopeBannerOverlay: { position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(17,24,39,0) 30%, rgba(17,24,39,0.75) 100%)", display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "10px", padding: "14px 16px" },
  scopeBannerText: { color: "#fff", fontSize: "13px", fontWeight: 700, maxWidth: "70%", lineHeight: 1.4 },
  scopeDetailsBtn: { display: "inline-flex", alignItems: "center", gap: "6px", background: "#fff", color: "#4f46e5", border: "none", borderRadius: "999px", padding: "8px 14px", fontSize: "12px", fontWeight: 800, cursor: "pointer", flexShrink: 0 },

  modalOverlay: { position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px", zIndex: 1000 },
  modalCard: { position: "relative", background: "#fff", borderRadius: "20px", padding: "26px", maxWidth: "560px", width: "100%", maxHeight: "85vh", overflowY: "auto" },
  modalCloseBtn: { position: "absolute", top: "16px", right: "16px", width: "30px", height: "30px", borderRadius: "50%", border: "none", background: "#f1f5f9", color: "#374151", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" },
  modalTitle: { margin: "0 0 20px", fontSize: "19px", fontWeight: 900, paddingRight: "30px" },
  modalSection: { marginBottom: "22px" },
  modalSectionHead: { display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", fontWeight: 800, color: "#111827", marginBottom: "14px", textTransform: "uppercase", letterSpacing: "0.03em" },
  modalCatRow: { display: "flex", gap: "12px", marginBottom: "14px", alignItems: "flex-start" },
  modalCatImg: { width: "64px", height: "64px", objectFit: "cover", borderRadius: "12px", flexShrink: 0 },
  modalCatName: { fontSize: "13px", fontWeight: 700, color: "#111827", marginBottom: "4px" },
  modalCatList: { margin: 0, paddingLeft: "18px", display: "flex", flexDirection: "column", gap: "4px" },
  modalCatPoint: { fontSize: "12.5px", color: "#374151", lineHeight: 1.5 },
  modalNote: { display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "#6b7280", marginTop: "4px" },

  planGrid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: "16px", marginBottom: "18px" },
  planCard: { position: "relative", border: "1.5px solid #e5e7eb", borderRadius: "16px", padding: "20px", background: "#fff" },
  planCardActive: { borderColor: "#4f46e5", boxShadow: "0 0 0 3px #eef2ff" },
  planBadge: { position: "absolute", top: "-10px", right: "16px", background: "#4f46e5", color: "#fff", fontSize: "11px", fontWeight: 800, padding: "4px 10px", borderRadius: "999px" },
  sizeNote: { display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", fontWeight: 700, color: "#4f46e5", background: "#eef2ff", padding: "8px 12px", borderRadius: "10px", marginBottom: "16px" },
  planName: { fontSize: "16px", fontWeight: 800, marginBottom: "4px" },
  planPrice: { fontSize: "26px", fontWeight: 900, color: "#4f46e5", marginBottom: "8px" },
  planMetaRow: { display: "flex", gap: "12px", fontSize: "11px", fontWeight: 700, color: "#6b7280", marginBottom: "10px" },
  planMetaItem: { display: "inline-flex", alignItems: "center", gap: "4px" },
  planDesc: { fontSize: "12px", color: "#6b7280", lineHeight: 1.5, margin: "0 0 14px" },
  planFeatureList: { listStyle: "none", padding: 0, margin: "0 0 16px", display: "flex", flexDirection: "column", gap: "8px" },
  planFeature: { display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "#374151" },
  planSelectBtn: { width: "100%", padding: "11px 0", borderRadius: "10px", border: "1.5px solid #4f46e5", background: "#fff", color: "#4f46e5", fontWeight: 700, fontSize: "13px", cursor: "pointer" },
  planSelectBtnActive: { background: "#4f46e5", color: "#fff" },

  trustStrip: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: "10px", marginBottom: "22px" },
  trustItem: { display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", fontWeight: 600, color: "#4b5563", background: "#f8fafc", border: "1px solid #eef2f7", borderRadius: "10px", padding: "10px 12px" },

  doDontGrid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: "20px", marginBottom: "20px" },
  doDontColTitleInclude: { display: "flex", alignItems: "center", gap: "6px", fontSize: "14px", fontWeight: 800, color: "#4f46e5", marginBottom: "10px" },
  doDontColTitleExclude: { display: "flex", alignItems: "center", gap: "6px", fontSize: "14px", fontWeight: 800, color: "#dc2626", marginBottom: "10px" },
  doDontList: { margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: "8px", listStyle: "none" },
  doDontItem: { display: "flex", alignItems: "flex-start", gap: "8px", fontSize: "13px", color: "#374151", lineHeight: 1.5 },
  notesBox: { background: "#f8fbff", border: "1px solid #e7eef9", borderRadius: "14px", padding: "16px", marginBottom: "16px" },
  notesBoxTitle: { display: "flex", alignItems: "center", gap: "6px", fontSize: "14px", fontWeight: 800, color: "#111827", marginBottom: "10px" },
  notesList: { margin: 0, padding: "0 0 0 18px", display: "flex", flexDirection: "column", gap: "8px", fontSize: "13px", color: "#374151", lineHeight: 1.5 },
  notesItem: { paddingLeft: "2px" },

  chipRow: { display: "flex", flexWrap: "wrap", gap: "8px" },
  dateChip: { display: "inline-flex", alignItems: "center", gap: "6px", padding: "10px 16px", borderRadius: "999px", border: "1.5px solid #e5e7eb", background: "#fff", fontSize: "13px", fontWeight: 700, color: "#374151", cursor: "pointer" },
  dateChipActive: { borderColor: "#4f46e5", background: "#4f46e5", color: "#fff" },
  dateInput: { marginTop: "10px", padding: "10px 14px", borderRadius: "10px", border: "1.5px solid #e5e7eb", fontSize: "13px" },

  slotGrid: { display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: "10px" },
  slotChip: { padding: "14px 0", borderRadius: "12px", border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151", cursor: "pointer", textAlign: "center" },
  // Navy blue once the customer books/selects a specific time slot — same
  // convention RapidCleaningPage.js uses.
  slotChipActive: { borderColor: "#1e3a8a", background: "#1e3a8a", color: "#fff" },
  slotChipLabel: { fontSize: "13px", fontWeight: 700, color: "inherit" },

  scheduleInfoRow: { display: "flex", gap: "10px", marginBottom: "22px", flexWrap: "wrap" },
  scheduleInfoChip: { display: "inline-flex", alignItems: "center", gap: "6px", background: "#eef2ff", color: "#3730a3", fontSize: "12px", fontWeight: 700, padding: "8px 12px", borderRadius: "999px" },

  locationBtn: { display: "inline-flex", alignItems: "center", gap: "8px", padding: "12px 18px", borderRadius: "12px", border: "1.5px solid #4f46e5", background: "#eef2ff", color: "#4f46e5", fontWeight: 700, fontSize: "13px", cursor: "pointer", marginBottom: "10px" },

  addressList: { display: "flex", flexDirection: "column", gap: "10px" },
  addressRow: { display: "flex", alignItems: "center", gap: "10px", padding: "14px", borderRadius: "12px", border: "1.5px solid #e5e7eb", background: "#fff", cursor: "pointer", textAlign: "left" },
  addressRowActive: { borderColor: "#4f46e5", background: "#eef2ff" },
  addressIcon: { fontSize: "18px" },
  addressBody: { flex: 1 },
  addressLabel: { fontSize: "13px", fontWeight: 700, color: "#111827" },
  addressText: { fontSize: "12px", color: "#6b7280" },

  addAddressBtn: { display: "inline-flex", alignItems: "center", gap: "6px", background: "none", border: "1.5px dashed #c7d2fe", color: "#4f46e5", fontWeight: 700, fontSize: "13px", cursor: "pointer", padding: "12px 16px", borderRadius: "12px", width: "100%", justifyContent: "center", margin: "12px 0 22px" },
  addAddressForm: { border: "1.5px solid #e5e7eb", borderRadius: "14px", padding: "16px", marginBottom: "22px" },
  formActions: { display: "flex", gap: "10px", justifyContent: "flex-end" },
  secondaryBtn: { padding: "10px 16px", borderRadius: "10px", border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151", fontWeight: 700, fontSize: "13px", cursor: "pointer" },
  primaryBtnSm: { padding: "10px 18px", borderRadius: "10px", border: "none", background: "#4f46e5", color: "#fff", fontWeight: 700, fontSize: "13px", cursor: "pointer" },
  errorText: { fontSize: "12px", color: "#dc2626", marginBottom: "10px" },
  errorBox: { background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626", fontSize: "13px", padding: "12px 14px", borderRadius: "10px", marginBottom: "16px" },
  stateBox: { fontSize: "13px", color: "#6b7280", background: "#f8fafc", border: "1px solid #eef2f7", borderRadius: "10px", padding: "12px 14px", marginBottom: "16px" },
  inlineLink: { color: "#4f46e5", fontWeight: 700, textDecoration: "none" },

  reviewLayout: { display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: "22px", alignItems: "start" },
  reviewLeft: { display: "flex", flexDirection: "column", gap: "14px" },
  reviewRight: { position: "sticky", top: "20px" },

  recapCard: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "16px", padding: "16px 18px" },
  recapHead: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" },
  recapTitle: { display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", fontWeight: 800, color: "#111827" },
  changeLink: { display: "inline-flex", alignItems: "center", gap: "4px", background: "none", border: "none", color: "#4f46e5", fontWeight: 700, fontSize: "12px", cursor: "pointer", padding: 0 },
  recapText: { fontSize: "13px", color: "#374151", lineHeight: 1.5 },
  recapSubText: { display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", color: "#6b7280", marginTop: "6px" },

  paymentOptions: { display: "flex", flexDirection: "column", gap: "10px" },
  payOptionBtn: { display: "flex", alignItems: "center", gap: "10px", padding: "12px 14px", borderRadius: "12px", border: "1.5px solid #e5e7eb", background: "#fff", cursor: "pointer", textAlign: "left" },
  payOptionBtnActive: { borderColor: "#4f46e5", background: "#eef2ff" },
  payOptionBtnDisabled: { opacity: 0.5, cursor: "not-allowed" },
  payOptionTitle: { fontSize: "13px", fontWeight: 700, color: "#111827" },
  payOptionSub: { fontSize: "11px", color: "#6b7280" },

  termsWrap: { display: "flex", alignItems: "flex-start", gap: "8px", fontSize: "12px", color: "#6b7280", lineHeight: 1.5 },

  priceCard: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "18px", padding: "18px", overflow: "hidden" },
  priceCardImg: { width: "100%", aspectRatio: "1 / 1", objectFit: "cover", borderRadius: "12px", marginBottom: "12px" },
  priceCardTitle: { fontSize: "15px", fontWeight: 800, marginBottom: "12px" },
  billRow: { display: "flex", justifyContent: "space-between", fontSize: "13px", color: "#374151", marginBottom: "10px" },
  billDivider: { borderTop: "1px solid #e5e7eb", margin: "10px 0" },
  totalRow: { display: "flex", justifyContent: "space-between", fontSize: "18px", fontWeight: 900, marginBottom: "16px" },
  secureNote: { display: "flex", alignItems: "center", gap: "6px", justifyContent: "center", marginTop: "12px", fontSize: "11px", color: "#6b7280", textAlign: "center" },
};

export default DeepCleaningPage;
